import React from 'react';
import { Avatar, Tag, Typography } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { getInitial } from './utils';
import { TimezoneUtils } from '../../utils/timezoneUtils';

const { Text } = Typography;

/** 動畫 keyframes（只注入一次） */
const PULSE_STYLE_ID = 'chat-session-pulse-style';
if (typeof document !== 'undefined' && !document.getElementById(PULSE_STYLE_ID)) {
  const style = document.createElement('style');
  style.id = PULSE_STYLE_ID;
  style.textContent = `
    @keyframes chatSessionPulse {
      0%   { box-shadow: 0 0 0 0 rgba(82,196,26,0.7); }
      70%  { box-shadow: 0 0 0 7px rgba(82,196,26,0); }
      100% { box-shadow: 0 0 0 0 rgba(82,196,26,0); }
    }
    @keyframes chatSessionSlideIn {
      0%   { transform: translateX(-20px); opacity: 0; }
      60%  { transform: translateX(4px); opacity: 1; }
      100% { transform: translateX(0); opacity: 1; }
    }
    @keyframes chatSessionFlash {
      0%   { background-color: #e6f7ff; }
      40%  { background-color: #bae7ff; }
      100% { background-color: inherit; }
    }
    .chat-unread-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 20px;
      height: 20px;
      padding: 0 5px;
      border-radius: 10px;
      background: #25d366;
      color: #fff;
      font-size: 11px;
      font-weight: 700;
      line-height: 20px;
      animation: chatSessionPulse 1.6s ease-in-out infinite;
      flex-shrink: 0;
    }
    .chat-session-new {
      animation: chatSessionSlideIn 0.4s ease-out, chatSessionFlash 2s ease-out 0.4s;
    }
    .chat-session-new-badge {
      display: inline-block;
      background: #1677ff;
      color: #fff;
      font-size: 10px;
      font-weight: 700;
      padding: 1px 5px;
      border-radius: 8px;
      margin-left: 4px;
      vertical-align: middle;
      animation: chatSessionPulse 1.6s ease-in-out infinite;
    }
  `;
  document.head.appendChild(style);
}

/**
 * 左側 session 列表的單一項目。
 * unreadCount > 0 時右側顯示 WhatsApp 風格綠色未讀數字 + 脈動動畫。
 * extraInfo 為選用補充行（Inbox 模式下顯示所屬 workflow/task）。
 */
const ChatSessionListItem = ({
  session,
  isActive,
  interventionCount = 0,
  unreadCount = 0,
  onClick,
  extraInfo,
  isNew = false
}) => {
  const { t } = useLanguage();
  const totalCount =
    (Array.isArray(session.messages) ? session.messages.length : (session.messageCount ?? 0)) +
    interventionCount;

  const hasUnread = unreadCount > 0;

  return (
    <div
      onClick={onClick}
      className={isNew ? 'chat-session-new' : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        cursor: 'pointer',
        borderBottom: '1px solid #f0f0f0',
        background: isActive ? '#e6f4ff' : hasUnread ? '#f6ffed' : '#fff',
        transition: 'background 120ms',
        position: 'relative'
      }}
    >
      {/* 左側細條：未讀時顯示綠色 */}
      {hasUnread && !isActive && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            background: '#25d366',
            borderRadius: '0 2px 2px 0'
          }}
        />
      )}

      {/* 頭像 */}
      <Avatar
        size={40}
        src={session.contactAvatarUrl || undefined}
        style={{ backgroundColor: '#25d366', flexShrink: 0 }}
        icon={!session.contactAvatarUrl ? <UserOutlined /> : undefined}
      >
        {!session.contactAvatarUrl
          ? getInitial(session.contactName || session.waId)
          : null}
      </Avatar>

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* 第一行：名稱 + 時間 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'center' }}>
          <Text
            strong={hasUnread || isNew}
            ellipsis
            style={{ maxWidth: isNew ? 110 : 130, fontWeight: (hasUnread || isNew) ? 700 : 400 }}
          >
            {session.contactName || session.waId}
          </Text>
          {isNew && (
            <span className="chat-session-new-badge">新</span>
          )}
          <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
            {session.updatedAt ? TimezoneUtils.formatDateTime(session.updatedAt, 'MM/DD HH:mm') : ''}
          </Text>
        </div>

        {/* 第二行：最新訊息預覽 + 右側（未讀數 或 狀態 tag） */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 6,
            marginTop: 2
          }}
        >
          <Text
            type="secondary"
            ellipsis
            style={{ fontSize: 12, maxWidth: 150, fontWeight: hasUnread ? 600 : 400, color: hasUnread ? '#333' : undefined }}
          >
            {session.lastMessagePreview ||
              (session.contactName ? session.waId : session.aiStaffName || '')}
          </Text>

          {/* 未讀數：有新訊息時優先顯示；否則顯示狀態 tag */}
          {hasUnread && !isActive ? (
            <span className="chat-unread-badge">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          ) : (
            <Tag
              color={session.state === 'done' ? 'default' : 'processing'}
              style={{
                marginInlineEnd: 0,
                fontSize: 11,
                lineHeight: '16px',
                padding: '0 6px',
                flexShrink: 0
              }}
            >
              {session.state === 'done'
                ? t('workflowMonitor.chatBotStateDone') || '已結束'
                : t('workflowMonitor.chatBotStateCollecting') || '進行中'}
            </Tag>
          )}
        </div>

        {/* 第三行：補充資訊（workflow/task，Inbox 模式） */}
        {extraInfo && (
          <Text type="secondary" ellipsis style={{ fontSize: 11, display: 'block', marginTop: 1 }}>
            {extraInfo}
          </Text>
        )}

        {/* 第四行：訊息數（非 Inbox 模式或無 lastPreview 時顯示） */}
        {!session.lastMessagePreview && totalCount > 0 && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            {totalCount} {t('workflowMonitor.chatBotMessages') || '則訊息'}
          </Text>
        )}
      </div>
    </div>
  );
};

export default ChatSessionListItem;
